import { useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'

import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import TextField from '@mui/material/TextField'

import { deleteDoc, existDoc, getDocs, setDoc, updateDoc, where } from 'lib/firestore'
import { Role } from 'utils/constant'

export default function MemberDialog({ account, locations, data, onClose }) {
  const {
    control,
    handleSubmit,
    watch,
    setError,
    formState: { errors, isSubmitting },
  } = useForm({ defaultValues: data })
  const [open, setOpen] = useState(false)
  const role = watch('role')

  useEffect(() => {
    setOpen(true)
  }, [])

  const getOwnerCount = () => {
    return getDocs(
      `accounts/${account.id}/members`,
      where('role', '==', Role.OWNER.value),
      where('uid', '>', '')
    ).then(docs => docs.length)
  }

  const onSubmit = async values => {
    if (data.id) {
      // 最後のアカウントオーナーの権限は変更できない
      if (data.uid !== null && data.role === Role.OWNER.value && values.role !== Role.OWNER.value) {
        const ownerCount = await getOwnerCount()
        if (ownerCount === 1) {
          setError('role', { message: '最後のアカウントオーナーの権限は変更できません' })
          return
        }
      }

      delete values.email
      if (values.role !== Role.LOCATION_MEMBER.value) {
        values.locationId = null
      }

      return updateDoc(`accounts/${account.id}/members/${data.id}`, values).then(() =>
        setOpen(false)
      )
    } else {
      // 既存メンバーは再招待できない
      const exist = await existDoc(`accounts/${account.id}/members/${values.email}`)
      if (exist) {
        setError('email', { message: '既に登録されています' })
        return
      }

      if (values.role === '') {
        setError('role', { message: '必須項目です' })
        return
      }

      if (values.role === 'location-member' && values.locationId === '') {
        setError('locationId', { message: '必須項目です' })
        return
      }

      return setDoc(`accounts/${account.id}/members/${values.email}`, {
        ...values,
        uid: null,
        account: { id: account.id, name: account.name },
        notify: { monthly: true, daily: true },
      }).then(() => setOpen(false))
    }
  }

  const handleDelete = async () => {
    // 最後のアカウントオーナーは削除できない
    if (data.uid !== null && data.role === Role.OWNER.value) {
      const ownerCount = await getOwnerCount()
      if (ownerCount === 1) {
        setError('role', { message: '最後のアカウントオーナーは削除できません' })
        return
      }
    }

    await deleteDoc(`accounts/${account.id}/members/${data.id}`)
    setOpen(false)
  }

  const priority = useMemo(() => {
    return Object.values(Role).find(x => x.value === account.role).priority
  }, [account])

  const canSubmit = useMemo(() => {
    if (!data.id) {
      return true
    }
    if (account.role === Role.LOCATION_MEMBER.value) {
      return false
    }
    const targetPriority = Object.values(Role).find(x => x.value === data.role).priority
    return priority >= targetPriority
  }, [data, priority, account])

  const canDelete = useMemo(() => {
    if (!data.id) {
      return false
    }
    const targetPriority = Object.values(Role).find(x => x.value === data.role).priority
    return priority >= targetPriority
  }, [data, priority])

  return (
    <Dialog open={open} onClose={() => setOpen(false)} TransitionProps={{ onExited: onClose }}>
      <DialogTitle>メンバー{data.id ? '情報' : '招待'}</DialogTitle>
      <DialogContent>
        <Controller
          name="email"
          control={control}
          rules={{
            required: 'メールアドレスを入力してください',
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: 'メールアドレスの形式で入力してください',
            },
          }}
          defaultValue=""
          render={({ field }) => (
            <TextField
              {...field}
              sx={{ mb: 2 }}
              label="メールアドレス"
              disabled={isSubmitting || Boolean(data.id)}
              error={Boolean(errors.email)}
              helperText={errors.email && errors.email.message}
            />
          )}
        />
        <Controller
          name="role"
          control={control}
          defaultValue=""
          render={({ field }) => (
            <TextField
              {...field}
              label="権限"
              sx={{ mb: 2 }}
              select
              disabled={!canSubmit}
              SelectProps={{ native: true }}
              error={Boolean(errors.role)}
              helperText={errors.role && errors.role.message}
            >
              <option></option>
              {Object.values(Role).map(x => (
                <option key={x.value} value={x.value} disabled={priority < x.priority}>
                  {x.name}：{x.description}
                </option>
              ))}
            </TextField>
          )}
        />
        {role === Role.LOCATION_MEMBER.value && locations.length > 0 && (
          <Controller
            name="locationId"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                label="拠点"
                sx={{ mb: 2 }}
                select
                SelectProps={{ native: true }}
                error={Boolean(errors.locationId)}
                helperText={errors.locationId && errors.locationId.message}
                disabled={!canSubmit}
              >
                <option></option>
                {locations.map(x => (
                  <option key={x.id} value={x.id}>
                    {x.name}
                  </option>
                ))}
              </TextField>
            )}
          />
        )}
      </DialogContent>
      <DialogActions>
        {canDelete && (
          <Button color="secondary" onClick={handleDelete}>
            削除する
          </Button>
        )}
        <div style={{ flex: 1 }} />
        <Button color="primary" onClick={() => setOpen(false)}>
          キャンセル
        </Button>
        <Button color="primary" onClick={handleSubmit(onSubmit)} disabled={!canSubmit}>
          {data.id ? '更新する' : '招待する'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
